import { IconStar } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { SupportedLanguage } from "../../interfaces";
import { resolveTopSessionsHeading } from "../../domains/congress/topSessionsHeading";
import { Avatar } from "../ui/Avatar";

function initialsOf(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  return letters === "" ? "?" : letters.slice(0, 2);
}

export function CongressTopSessionsCard({
  cardTitle,
  contentLang,
  shortName,
  curatorName,
  curatorRole,
  curatorAvatarUrl,
  sessionCount,
  onOpen,
}: {
  /** Admin-authored heading for `contentLang`; null → the app's own label */
  cardTitle: string | null;
  /** Content language of the sessions this card heads */
  contentLang: SupportedLanguage;
  shortName: string;
  curatorName: string | null;
  /** Admin-authored role printed above the name ("Curator", "Chair", …) */
  curatorRole: string;
  curatorAvatarUrl: string | null;
  sessionCount: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer flex-col gap-2 rounded-2xl bg-secondary-green-2 p-4 text-left font-dm-sans"
    >
      {/* The star labels the heading, so it aligns to the heading's FIRST LINE,
          not to the centre of the whole block — the block's height varies with
          wrapping ("LES 5 SESSIONS INCONTOURNABLES DE L'EEC" takes two lines at
          390px) while the icon's meaning attaches to the start of the text.

          `items-start` alone is NOT enough: it would align the icon to the top of
          the text box, above the optical centre of the caps, and read as
          floating. It is paired with a wrapper whose HEIGHT IS THE HEADING'S
          LINE-HEIGHT and which centres the icon inside itself, so the icon's
          centre coincides with line one's centre. Not baseline-aligned: an icon
          has no baseline that reads naturally and a star ends up sitting low.

          `--tsh-leading` is the single source of that line-height, consumed by
          BOTH the wrapper's height and the heading's `leading`. It must stay
          derived: writing the wrapper height as a px constant would silently
          break this alignment the next time the leading is tuned.

          That leading is 1rem (16px) on 14px caps, ratio 1.14 — tightened from
          the 1.25rem `text-sm` applies, because caps have no descenders and
          leading tuned for mixed-case text reads too airy in uppercase. Scoped
          here via the local var, NOT a shared token or a global line-height,
          which would also affect mixed-case text where 1.25rem is correct.

          Consequence of the two together: the 20px star exceeds the 16px line box
          and overflows 2px above and below. Symmetric, so its centre still
          coincides with line one's, and unclipped — neither this card nor the page
          block sets overflow-hidden, and there is 16px of padding above.
          Deliberately NOT shrunk to size-4, which would cut the icon's visual
          weight by 20% to solve something nobody can see. */}
      <div className="flex items-start gap-2 text-ds-mint-800 [--tsh-leading:1rem]">
        <span className="flex h-(--tsh-leading) shrink-0 items-center">
          <IconStar className="size-5 stroke-[1.5px]" />
        </span>
        <p className="m-0 text-sm font-semibold uppercase leading-(--tsh-leading) tracking-wide">
          {resolveTopSessionsHeading({
            cardTitle,
            shortName,
            sessionCount,
            contentLang,
            t,
          })}
        </p>
      </div>
      {curatorName !== null && (
        <div className="flex items-center gap-2">
          {/* 52px (`lg`) is the Figma spec for the curator photo, and it is
              DELIBERATELY the same on both Top Sessions surfaces — this card and
              the page header (CongressTopSessionsPage).

              These two used to differ (`sm` 24px here, `md` 32px there). That
              asymmetry was incidental, not a card-vs-detail hierarchy, and it was
              removed on purpose: the client's complaint was that the photo read as
              minuscule on both. Please do NOT re-differentiate them to "restore"
              a hierarchy — keep the two in sync, or change the spec first.

              `lg` also scales the corner radius and the initials fallback, which
              is why this is a token swap and not a size override. */}
          {curatorAvatarUrl !== null ? (
            <Avatar
              variant="image"
              src={curatorAvatarUrl}
              alt={curatorName}
              imageFit="cover"
              size="lg"
              badge={null}
            />
          ) : (
            <Avatar
              variant="initials"
              initials={initialsOf(curatorName)}
              size="lg"
              badge={null}
            />
          )}
          <div className="flex min-w-0 flex-col">
            <span className="text-xs uppercase tracking-wide text-ds-slate-500">
              {curatorRole}
            </span>
            <span className="truncate text-sm font-semibold text-ds-black">
              {curatorName}
            </span>
          </div>
        </div>
      )}
      <span className="text-sm font-medium text-ds-mint-700">
        {t("Tap to view details")}
      </span>
    </button>
  );
}
